import { FC, useState, useEffect, useCallback } from 'react';
import { Form, InputNumber, Button, Slider, Space, message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import ProductSelector from '../ProductSelector';
import RealTimePriceMonitor from '../RealTimePriceMonitor';
import { previewSimulation, executeSimulation, SimulationResult, deactivateSimulation } from '../../api/priceSimulation';
import { SimulationParams } from '../../api/priceSimulation';
import { fetchRealTimePrice } from '../../api/priceOffset';

interface SimulationFormProps {
  onPreview?: (data: any[]) => void;
  onSuccess?: () => void;
  initialValues?: SimulationResult | null;
  onReset?: () => void;
}

const SimulationForm: FC<SimulationFormProps> = ({ onPreview, onSuccess, initialValues, onReset }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [previewDisabled, setPreviewDisabled] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>();
  const [currentPrice, setCurrentPrice] = useState<number>(0);

  // 当初始值变化时，重置表单
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        productCode: initialValues.productCode,
        finalPrice: initialValues.finalPrice,
        peakTimeSeconds: initialValues.peakTimeSeconds,
        peakDurationSeconds: initialValues.peakDurationSeconds,
        totalDurationSeconds: initialValues.totalDurationSeconds,
        amplitudeFactor: initialValues.amplitudeFactor
      });
      setSelectedProduct(initialValues.productCode);
    } else {
      form.resetFields();
      setSelectedProduct(undefined);
    }
  }, [initialValues, form]);

  // 获取当前价格
  useEffect(() => {
    if (selectedProduct) {
      const fetchPrice = async () => {
        try {
          const priceData = await fetchRealTimePrice(selectedProduct);
          setCurrentPrice(priceData.controlPrice || priceData.price || 0);
        } catch (error) {
          console.error('获取当前价格失败', error);
        }
      };
      fetchPrice();
    }
  }, [selectedProduct]);

  // 处理产品选择变化
  const handleProductChange = (value: string) => {
    setSelectedProduct(value);
  };

  // 检查价格是否超出1%范围
  const checkPriceRange = (finalPrice: number): boolean => {
    if (!currentPrice || currentPrice === 0) return false;
    
    const upperLimit = currentPrice * 1.01; // 上限：当前价格的101%
    const lowerLimit = currentPrice * 0.99; // 下限：当前价格的99%
    
    return finalPrice > upperLimit || finalPrice < lowerLimit;
  };

  // 显示二次确认弹窗
  const showConfirmModal = (values: SimulationParams, isPreview: boolean = false) => {
    const changePercent = (values.finalPrice - currentPrice) / currentPrice * 100;
    
    Modal.confirm({
      title: '目标价格超出1%',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>当前价格：{currentPrice.toFixed(4)}</p>
          <p>目标价格：{values.finalPrice.toFixed(4)}</p>
          <p>变动幅度：{changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%</p>
          <p style={{ color: '#ff4d4f', marginTop: 16 }}>
            目标价格变动幅度超过1%，请确认是否继续{isPreview ? '预览' : '执行'}？
          </p>
        </div>
      ),
      okText: `确认${isPreview ? '预览' : '执行'}`,
      cancelText: '取消',
      onOk: () => isPreview ? executePreview(values) : executeSimulationAction(values)
    });
  };

  // 执行预览
  const executePreview = async (values: SimulationParams) => {
    console.log('预览参数:', values);
    setPreviewing(true);

    try {
      const data = await previewSimulation(values);
      console.log('预览响应数据:', data);
      
      // 检查响应数据是否符合预期
      if (Array.isArray(data)) {
        console.log('有效预览数据:', data);
        onPreview?.(data);
      } else if (data && typeof data === 'object' && 'data' in (data as any)) {
        // 处理可能的嵌套响应格式: { code: 0, message: 'ok', data: [...] }
        console.log('嵌套预览数据:', (data as any).data);
        onPreview?.((data as any).data);
      } else {
        console.error('预览数据格式异常:', data);
        message.error('获取预览数据格式异常');
      }
    } catch (error) {
      console.error('获取预览数据失败:', error);
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('获取预览数据失败');
      }
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreviewInternal = async () => {
    try {
      const values = await form.validateFields();
      
      // 检查价格范围
      if (checkPriceRange(values.finalPrice)) {
        showConfirmModal(values, true);
      } else {
        executePreview(values);
      }
    } catch (error) {
      console.error('表单验证失败:', error);
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  // 防抖函数
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // 执行模拟
  const executeSimulationAction = async (values: SimulationParams) => {
    console.log('执行模拟参数:', values);
    setSubmitting(true);
    try {
      const result = await executeSimulation(values);
      console.log('执行模拟结果:', result);
      message.success('执行价格模拟成功');
      onSuccess?.();
    } catch (error) {
      console.error('执行价格模拟失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitInternal = async (values: SimulationParams) => {
    // 检查价格范围
    if (checkPriceRange(values.finalPrice)) {
      showConfirmModal(values, false);
    } else {
      executeSimulationAction(values);
    }
  };

  // 防抖处理的预览函数
  const handlePreview = useCallback(
    debounce(() => {
      setPreviewDisabled(true);
      handlePreviewInternal().finally(() => {
        setTimeout(() => setPreviewDisabled(false), 1000); // 1秒防抖
      });
    }, 300),
    []
  );

  // 防抖处理的提交函数
  const handleSubmit = useCallback(
    debounce((values: SimulationParams) => {
      setSubmitDisabled(true);
      handleSubmitInternal(values).finally(() => {
        setTimeout(() => setSubmitDisabled(false), 2000); // 2秒防抖
      });
    }, 500),
    []
  );

  const handleDeactivate = async () => {
    const productCode = form.getFieldValue('productCode');
    if (!productCode) {
      message.warning('请先选择产品');
      return;
    }

    console.log('取消激活模拟产品:', productCode);
    setSubmitting(true);
    try {
      const result = await deactivateSimulation(productCode);
      console.log('取消激活模拟结果:', result);
      message.success(`产品 ${productCode} 的价格模拟已取消激活`);
      onSuccess?.();
    } catch (error) {
      console.error('取消激活价格模拟失败', error);
      message.error(`取消激活产品 ${productCode} 的价格模拟失败，请稍后重试`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <RealTimePriceMonitor productCode={selectedProduct} />
      
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="选择产品"
          name="productCode"
          rules={[{ required: true, message: '请选择产品' }]}
          extra={<span style={{ fontSize: '12px', color: '#999' }}>提示：休盘的产品已被过滤，不会在列表中显示</span>}
        >
          <ProductSelector onChange={handleProductChange} />
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.productCode !== curr.productCode}>
          {({ getFieldValue }) => {
            const productCode = getFieldValue('productCode');
            if (productCode && productCode !== selectedProduct) {
              setSelectedProduct(productCode);
            }
            return null;
          }}
        </Form.Item>
        
        <Form.Item 
          label="目标价格" 
          name="finalPrice" 
          rules={[{ required: true, message: '请输入目标价格' }]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate={(prev, curr) => 
          prev.peakTimeSeconds !== curr.peakTimeSeconds ||
          prev.peakDurationSeconds !== curr.peakDurationSeconds ||
          prev.totalDurationSeconds !== curr.totalDurationSeconds
        }>
          {({ getFieldValue }) => {
            const formatSeconds = (seconds: number) => {
              if (!seconds || seconds < 0) return '请设置时间';
              const hours = Math.floor(seconds / 3600);
              const minutes = Math.floor((seconds % 3600) / 60);
              const remainingSeconds = seconds % 60;
              const parts: string[] = [];
              if (hours > 0) parts.push(`${hours}小时`);
              if (minutes > 0) parts.push(`${minutes}分钟`);
              if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}秒`);
              return parts.join('');
            };

            return (
              <>
                <Form.Item 
                  label={
                    <Space direction="vertical" size={0}>
                      <span>到达顶点时间(秒)</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {formatSeconds(getFieldValue('peakTimeSeconds'))}
                      </span>
                    </Space>
                  }
                  name="peakTimeSeconds" 
                  initialValue={300}
                  rules={[
                    { required: true, message: '请设置到达顶点时间' },
                    { type: 'number', min: 1, max: 1800, message: '到达顶点时间必须在1-1800秒之间' }
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={1800} />
                </Form.Item>
                
                <Form.Item 
                  label={
                    <Space direction="vertical" size={0}>
                      <span>顶点保持时间(秒)</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {formatSeconds(getFieldValue('peakDurationSeconds'))}
                      </span>
                    </Space>
                  }
                  name="peakDurationSeconds"
                  initialValue={120} 
                  rules={[
                    { required: true, message: '请设置顶点保持时间' },
                    { type: 'number', min: 0, max: 1800, message: '顶点保持时间必须在0-1800秒之间' }
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={1800} />
                </Form.Item>
                
                <Form.Item 
                  label={
                    <Space direction="vertical" size={0}>
                      <span>总持续时间(秒)</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {formatSeconds(getFieldValue('totalDurationSeconds'))}
                      </span>
                    </Space>
                  }
                  name="totalDurationSeconds"
                  initialValue={600} 
                  rules={[
                    { required: true, message: '请设置总持续时间' },
                    { type: 'number', min: 1, max: 3600, message: '总持续时间必须在1-3600秒之间' }
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={3600} />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>
        
        <Form.Item 
          label="振幅因子" 
          name="amplitudeFactor"
          initialValue={5} 
          rules={[{ required: true, message: '请设置振幅因子' }]}
        >
          <Slider min={0} max={10} marks={{ 0: '0', 5: '5', 10: '10' }} />
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button onClick={handlePreview} loading={previewing} disabled={previewDisabled}>预览曲线</Button>
            <Button type="primary" htmlType="submit" loading={submitting} disabled={submitDisabled}>执行模拟</Button>
            {initialValues?.isActive && (
              <Button onClick={handleDeactivate} loading={submitting}>取消激活</Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};

export default SimulationForm; 